//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { rightsOwnedOrPendingModalActions } from '@galleries/actions/rightsOwnedOrPendingModalActions';
import { MediaGrid } from '@galleries/components/content/media-grid/MediaGrid';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { pluralize } from '@utils/stringUtils';

export const RightsOwnedModal = () => {
  const dispatch = useDispatch();

  const { isOpen, numTotalItems, rightsOwnedItems } = useSelector(
    (state) => state.galleries.rightsOwnedOrPendingModal,
  );

  const rightsOwnedImages = rightsOwnedItems.map((rightsOwnedItem, index) => {
    return (
      <ImageContainer key={index}>
        <StyledImage
          alt={rightsOwnedItem.description}
          src={rightsOwnedItem.imageUrl}
        />
      </ImageContainer>
    );
  });

  const rightsOwnedOnAllItems = rightsOwnedItems.length === numTotalItems;
  const itemCounts = rightsOwnedOnAllItems
    ? `all selected items`
    : `${rightsOwnedItems.length} of the ${pluralize(numTotalItems, 'item')}`;

  return (
    <Modal
      closeModal={() => dispatch(rightsOwnedOrPendingModalActions.closeModal())}
      contents={
        <>
          <BodyText data-testid='rights-owned-modal-p'>
            Hooray! You already have or have requested rights on {itemCounts}.
            They have been successfully added to Galleries
            {rightsOwnedOnAllItems
              ? '.'
              : ' and will be removed from the next step.'}
          </BodyText>
          <StyledMediaGrid forceWidth={4}>{rightsOwnedImages}</StyledMediaGrid>
        </>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        onClick: () => dispatch(rightsOwnedOrPendingModalActions.closeModal()),
        buttonContent: 'Okay',
      }}
      shouldIncludeSecondaryButton={false}
      title={`You already have rights for ${pluralize(rightsOwnedItems.length, 'item')}`}
      width='wide'
    />
  );
};

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const StyledMediaGrid = styled(MediaGrid)`
  min-width: 100%;
  max-height: 335px;
  overflow-y: scroll;
`;

const ImageContainer = styled.div`
  width: 100%;
`;

angular.module('curalate').component('rightsOwnedModal', {
  controller: makeAngularReactController(<RightsOwnedModal />),
});
